import React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { IconSparkles } from '@tabler/icons-react';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useTranslation } from 'react-i18next';
import GradientButton from '../patterns/motion/GradientButton';
import LoopVideoBlock from '../video/LoopVideoBlock';
import colors from '../../designTokens/colors.esm';

// 연말정산 영상 URL (YearEndHeroSection과 동일)
const YEAR_REVIEW_VIDEO = 'https://firebasestorage.googleapis.com/v0/b/my-threads-insight.firebasestorage.app/o/yearEnd%2F2025%2Fyear_review.mp4?alt=media';

/**
 * YearEndBanner - 연말정산 참여 유도 배너
 * DiscordCommunityBanner와 동일한 디자인 시스템 적용
 */
const YearEndBanner = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useIsMobile('sm');
  const { t } = useTranslation();

  const handleClick = () => {
    navigate('/year-end');
  };

  return (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      sx={{
        width: '100%',
        borderRadius: '24px',
        overflow: 'hidden',
        border: `0.5px solid ${theme.palette.text.primary}`,
        height: isMobile ? 'auto' : '240px',
      }}
    >
      {/* 텍스트 영역 */}
      <Stack
        gap={4}
        sx={{
          flex: 1,
          p: isMobile ? 3 : 4,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
        }}
      >
        <Stack spacing={0.5}>
          <Typography
            variant={isMobile ? 'h6' : 'h5'}
            fontWeight={800}
            color="text.primary"
          >
            {t('yearEndBanner.title')}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              maxWidth: isMobile ? '100%' : '400px',
              lineHeight: 1.6,
              mb: 1,
            }}
          >
            {t('yearEndBanner.description')}
          </Typography>
        </Stack>

        {/* 모바일에서만 보이는 비디오 블록 */}
        {isMobile && (
          <Box
            sx={{
              width: '100%',
              height: '200px',
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: '100%',
                transform: 'scale(1)',
                transformOrigin: 'center',
              }}
            >
              <LoopVideoBlock
                videoUrl={YEAR_REVIEW_VIDEO}
                isMobile={isMobile}
                width="100%"
                height="100%"
                enableHover={false}
                playOnHover={false}
                borderRadius={0}
              />
            </Box>
          </Box>
        )}

        <GradientButton
          angle={90}
          colors={[colors.inkGray[9], colors.inkGray[7], colors.inkGray[9]]}
          size="large"
          onClick={handleClick}
          fullWidth={false}
          animationDuration="4s"
          sx={{
            fontWeight: 600,
            textTransform: 'none',
            width: 'fit-content',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconSparkles size={20} color="white" />
            {t('yearEndBanner.button')}
          </Box>
        </GradientButton>
      </Stack>

      {/* 비디오 영역 - 데스크톱에서만 보임 */}
      {!isMobile && (
        <Box
          sx={{
            width: '240px',
            height: '240px',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              transform: 'scale(1)',
              transformOrigin: 'center',
            }}
          >
            <LoopVideoBlock
              videoUrl={YEAR_REVIEW_VIDEO}
              isMobile={isMobile}
              width="100%"
              height="100%"
              enableHover={false}
              playOnHover={false}
              borderRadius={0}
            />
          </Box>
        </Box>
      )}
    </Stack>
  );
};

export default YearEndBanner;
